import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  BaseColumnConfig,
  ColumnFilterConfig,
  FilterState,
  FilterValue,
  SortPair,
  SortState,
  StandaloneFilter,
} from './types';

const RESERVED = new Set(['sort', 'page', 'search']);

function parseSortParam(raw: string | null): SortState {
  if (!raw) return [];
  return raw
    .split(',')
    .map((entry) => {
      const [columnId, direction = 'asc'] = entry.split(':');
      if (!columnId) return null;
      if (direction !== 'asc' && direction !== 'desc') return null;
      return { columnId, direction } as SortPair;
    })
    .filter((p): p is SortPair => p !== null);
}

function serializeSort(sort: SortState): string | null {
  if (sort.length === 0) return null;
  return sort.map((p) => `${p.columnId}:${p.direction}`).join(',');
}

function readFilters(
  filterables: { id: string; filter: ColumnFilterConfig }[],
  params: URLSearchParams,
  key: (name: string) => string,
): FilterState {
  const out: FilterState = {};
  for (const f of filterables) {
    const raw = params.get(key(f.id));
    if (raw === null) continue;
    if (f.filter.type === 'boolean') {
      out[f.id] = raw === 'true' ? true : raw === 'false' ? false : undefined;
    } else {
      out[f.id] = raw;
    }
  }
  return out;
}

export type GridUrlStateConfig<TRow> = {
  columns: BaseColumnConfig<TRow>[];
  filters?: StandaloneFilter[];
  defaultSort?: SortState;
  /**
   * Namespaces this grid's query params. Required when a page shows two grids
   * at once — without it both read and write the same `?sort=`/`?page=`.
   */
  paramPrefix?: string;
};

export type GridUrlState = {
  sort: SortState;
  filters: FilterState;
  search: string;
  page: number;
  setSort: (next: SortState) => void;
  setFilter: (columnId: string, value: FilterValue) => void;
  setSearch: (value: string) => void;
  setPage: (page: number) => void;
};

export function useGridUrlState<TRow>(config: GridUrlStateConfig<TRow>): GridUrlState {
  const [params, setParams] = useSearchParams();
  const prefix = config.paramPrefix ?? '';
  const key = useCallback((name: string) => `${prefix}${name}`, [prefix]);

  const sort = useMemo<SortState>(() => {
    const parsed = parseSortParam(params.get(key('sort')));
    return parsed.length > 0 ? parsed : (config.defaultSort ?? []);
  }, [params, config.defaultSort, key]);

  const filters = useMemo<FilterState>(() => {
    const filterables: { id: string; filter: ColumnFilterConfig }[] = [
      ...config.columns.filter((c) => c.filter).map((c) => ({ id: c.id, filter: c.filter! })),
      ...(config.filters ?? []),
    ];
    return readFilters(filterables, params, key);
  }, [config.columns, config.filters, params, key]);

  const search = useMemo<string>(() => params.get(key('search')) ?? '', [params, key]);

  const page = useMemo<number>(() => {
    const raw = params.get(key('page'));
    const n = raw ? Number(raw) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }, [params, key]);

  const setSort = useCallback(
    (next: SortState) => {
      const nextParams = new URLSearchParams(params);
      const serialized = serializeSort(next);
      if (serialized) nextParams.set(key('sort'), serialized);
      else nextParams.delete(key('sort'));
      nextParams.delete(key('page'));
      setParams(nextParams, { replace: false });
    },
    [params, setParams, key],
  );

  const setFilter = useCallback(
    (columnId: string, value: FilterValue) => {
      if (RESERVED.has(columnId)) {
        throw new Error(`Grid filter column id "${columnId}" is reserved.`);
      }
      const nextParams = new URLSearchParams(params);
      if (value === undefined || value === '') nextParams.delete(key(columnId));
      else nextParams.set(key(columnId), String(value));
      nextParams.delete(key('page'));
      setParams(nextParams, { replace: false });
    },
    [params, setParams, key],
  );

  const setPage = useCallback(
    (next: number) => {
      const nextParams = new URLSearchParams(params);
      if (next <= 1) nextParams.delete(key('page'));
      else nextParams.set(key('page'), String(next));
      setParams(nextParams, { replace: false });
    },
    [params, setParams, key],
  );

  const setSearch = useCallback(
    (value: string) => {
      const nextParams = new URLSearchParams(params);
      if (value === '') nextParams.delete(key('search'));
      else nextParams.set(key('search'), value);
      nextParams.delete(key('page'));
      setParams(nextParams, { replace: false });
    },
    [params, setParams, key],
  );

  return { sort, filters, search, page, setSort, setFilter, setSearch, setPage };
}
